import { CSSProperties, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import Search from '@mui/icons-material/Search';
import {
  Box,
  styled,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import NonIdealState from '../NonIdealState';
import Pagination from '../Pagination';
import { getComparator, MIN_ROW_HEIGHT, ROW_SIZE } from './helpers';
import SkeletonRows from './SkeletonRows';
import TableHead from './TableHead';
import { Order, RowData, TableProps } from './types';

export default function Table<T extends RowData = RowData>({
  columns,
  customStickyHeaderTopValue,
  defaultOrder = 'asc',
  defaultOrderBy,
  defaultPaginationValue,
  emptyStateProps,
  emptyTableHeight,
  infiniteScrollListener,
  isLoading = false,
  onClickRow,
  onClickSummaryRow,
  onSetPageCallback,
  onSortCallback,
  overscanCount,
  paginationValue,
  rows = [],
  rowsPerPage = 10,
  shouldDisablePagination,
  shouldHandleSort = true,
  shouldVirtualizeTableRows,
  summaryRow,
  tableBodyHeight,
  tableLayout = 'auto',
  tableRowStyles = {},
}: TableProps<T>) {
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<keyof T | null>(
    defaultOrderBy ?? null,
  );
  const [page, setPage] = useState(defaultPaginationValue ?? 1);

  // Controlled vs Uncontrolled mode
  const isPaginationControlled = typeof paginationValue === 'number';
  const pageValue = isPaginationControlled ? paginationValue : page;

  const isEmpty = !isLoading && rows.length === 0;
  const emptyRows =
    pageValue > 1 ? Math.max(0, pageValue * rowsPerPage - rows.length) : 0;
  const hasMultiplePages =
    !shouldDisablePagination && rows.length > rowsPerPage;

  const onRequestSort = (property: string) => {
    const isDesc = orderBy === property && order === 'desc';
    const newOrder = isDesc ? 'asc' : 'desc';
    setOrder(newOrder);
    setOrderBy(property);
    if (onSortCallback) {
      onSortCallback(property, newOrder);
    }
  };

  const tableData = useMemo(() => {
    const rowData = [...rows];

    if (shouldHandleSort && orderBy) {
      const column = columns.find(column => column.id === orderBy);
      const comparatorFunction = column?.comparatorFunction;
      rowData.sort(getComparator<T>(order, orderBy, comparatorFunction));
    }

    const startCursor = (pageValue - 1) * rowsPerPage;
    return rowData.slice(
      startCursor,
      shouldDisablePagination ? rows.length : startCursor + rowsPerPage,
    );
  }, [
    order,
    orderBy,
    pageValue,
    rows,
    rowsPerPage,
    shouldDisablePagination,
    shouldHandleSort,
    columns,
  ]);

  const Row = ({ index, style }: { index: number; style: CSSProperties }) => {
    const isVirtualizedSummaryRow = summaryRow && index === 0;
    const indexCountingSummaryRow = summaryRow ? index - 1 : index;
    const row = tableData[indexCountingSummaryRow];

    const tableRowStyle = {
      ...style,
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      minWidth: '100%',
      width: '100%',
    };

    return isVirtualizedSummaryRow ? (
      <TableRow
        hover={!!onClickSummaryRow}
        onClick={
          onClickSummaryRow ? () => onClickSummaryRow(summaryRow) : undefined
        }
        sx={[
          tableRowStyle,
          typeof tableRowStyles === 'function'
            ? tableRowStyles(summaryRow, true)
            : tableRowStyles,
        ]}
        component='div'
      >
        {columns.map(
          ({ align = 'left', cellRenderer, id, minWidth, sticky, width }) => {
            const cellData = cellRenderer
              ? cellRenderer(summaryRow, true)
              : summaryRow[id];
            return (
              <TableCell
                component='div'
                key={id}
                align={align}
                sx={[
                  theme => ({
                    backgroundColor: theme.palette.colors.blue[100],
                  }),
                  onClickSummaryRow ? { cursor: 'pointer' } : null,
                  {
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexBasis: width ?? 0,
                    flexGrow: width ? 0 : 1,
                    flexShrink: width ? 0 : 1,
                    height: ROW_SIZE,
                    justifyContent:
                      align === 'center' ? 'center' : 'flex-start',
                    left: sticky === 'left' ? 0 : undefined,
                    minWidth,
                    position: sticky ? 'sticky' : undefined,
                    right: sticky === 'right' ? 0 : undefined,
                    width,
                    zIndex: sticky ? 3 : undefined,
                  },
                ]}
              >
                {cellData}
              </TableCell>
            );
          },
        )}
      </TableRow>
    ) : (
      <TableRow
        hover={!!onClickRow}
        key={index}
        onClick={onClickRow ? () => onClickRow(row, index) : undefined}
        sx={[
          tableRowStyle,
          typeof tableRowStyles === 'function'
            ? tableRowStyles(row)
            : tableRowStyles,
        ]}
        component='div'
      >
        {columns.map(
          ({ align = 'left', cellRenderer, id, minWidth, sticky, width }) => {
            const cellData = cellRenderer ? cellRenderer(row) : row[id];
            return (
              <TableCell
                key={id}
                sx={[
                  theme => ({
                    backgroundColor: sticky
                      ? theme.palette.colors.white
                      : undefined,
                  }),
                  onClickRow ? { cursor: 'pointer' } : null,
                  {
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexBasis: width ?? 0,
                    flexGrow: width ? 0 : 1,
                    flexShrink: width ? 0 : 1,
                    height: ROW_SIZE,
                    justifyContent:
                      align === 'center' ? 'center' : 'flex-start',
                    left: sticky === 'left' ? 0 : undefined,
                    minWidth,
                    position: sticky ? 'sticky' : undefined,
                    right: sticky === 'right' ? 0 : undefined,
                    width,
                    zIndex: sticky ? 3 : undefined,
                  },
                ]}
                component='div'
                variant='body'
              >
                {cellData}
              </TableCell>
            );
          },
        )}
      </TableRow>
    );
  };

  return (
    <>
      <TableContainer
        sx={[shouldDisablePagination ? { overflowX: 'initial' } : null]}
      >
        <MuiTable
          sx={[
            theme => ({
              '& .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.colors.slate[100]}`,
                fontSize: '14px',
              },
              '& .MuiTableRow-hover': !!onClickRow
                ? {
                    '&:hover': {
                      backgroundColor: `${theme.palette.colors.blue[50]} !important`,
                    },
                  }
                : null,
              tableLayout,
            }),
            customStickyHeaderTopValue
              ? {
                  '& .MuiTableHead-root': {
                    position: 'sticky',
                    top: customStickyHeaderTopValue,
                    zIndex: 1,
                  },
                }
              : null,
          ]}
          stickyHeader={shouldDisablePagination}
          component={shouldVirtualizeTableRows ? 'div' : 'table'}
        >
          {!isLoading && (
            <TableHead
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={onRequestSort}
              shouldVirtualizeTableRows={shouldVirtualizeTableRows}
            />
          )}
          <TableBody
            sx={[
              shouldVirtualizeTableRows
                ? {
                    height: `calc(${
                      tableBodyHeight ?? '100vh'
                    } - ${ROW_SIZE}px)`,
                    width: '100%',
                  }
                : null,
            ]}
            component={shouldVirtualizeTableRows ? 'div' : 'tbody'}
          >
            {isLoading && (
              <SkeletonRows columns={columns} rowsPerPage={rowsPerPage} />
            )}
            {isEmpty && (
              <TableRow
                style={{
                  height: emptyTableHeight
                    ? emptyTableHeight
                    : MIN_ROW_HEIGHT * rowsPerPage,
                }}
              >
                <TableCell align='center' colSpan={columns.length}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <NonIdealState
                      icon={<Search />}
                      title='No results found'
                      {...emptyStateProps}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isEmpty && (
              <>
                {summaryRow && !shouldVirtualizeTableRows && (
                  <TableRow
                    hover={!!onClickSummaryRow}
                    onClick={
                      onClickSummaryRow
                        ? () => onClickSummaryRow(summaryRow)
                        : undefined
                    }
                    sx={[
                      typeof tableRowStyles === 'function'
                        ? tableRowStyles(summaryRow, true)
                        : tableRowStyles,
                    ]}
                  >
                    {columns.map(
                      ({
                        align = 'left',
                        cellRenderer,
                        id,
                        minWidth,
                        sticky,
                        width,
                      }) => {
                        const cellData = cellRenderer
                          ? cellRenderer(summaryRow, true)
                          : summaryRow[id];
                        return (
                          <TableCell
                            key={id}
                            align={align}
                            sx={[
                              theme => ({
                                backgroundColor: theme.palette.colors.blue[100],
                                minWidth,
                                width,
                              }),
                              onClickSummaryRow ? { cursor: 'pointer' } : null,
                              {
                                left: sticky === 'left' ? 0 : undefined,
                                position: sticky ? 'sticky' : undefined,
                                right: sticky === 'right' ? 0 : undefined,
                                zIndex: sticky ? 3 : undefined,
                              },
                            ]}
                          >
                            {cellData}
                          </TableCell>
                        );
                      },
                    )}
                  </TableRow>
                )}

                {shouldVirtualizeTableRows ? (
                  <AutoSizer>
                    {({ height = rowsPerPage, width = rowsPerPage }) => (
                      <List
                        height={height}
                        itemCount={
                          summaryRow ? tableData.length + 1 : tableData.length
                        }
                        itemSize={ROW_SIZE}
                        width={width}
                        overscanCount={overscanCount}
                      >
                        {Row}
                      </List>
                    )}
                  </AutoSizer>
                ) : (
                  tableData.map((row, idx) => {
                    return (
                      <TableRow
                        hover={!!onClickRow}
                        key={idx}
                        onClick={
                          onClickRow ? () => onClickRow(row, idx) : undefined
                        }
                        sx={[
                          typeof tableRowStyles === 'function'
                            ? tableRowStyles(row)
                            : tableRowStyles,
                        ]}
                      >
                        {columns.map(
                          ({
                            align = 'left',
                            cellRenderer,
                            id,
                            minWidth,
                            sticky,
                            width,
                          }) => {
                            const cellData = cellRenderer
                              ? cellRenderer(row)
                              : row[id];
                            return (
                              <TableCell
                                key={id}
                                align={align}
                                sx={[
                                  theme => ({
                                    backgroundColor: sticky
                                      ? theme.palette.colors.white
                                      : undefined,
                                  }),
                                  {
                                    left: sticky === 'left' ? 0 : undefined,
                                    minWidth,
                                    position: sticky ? 'sticky' : undefined,
                                    right: sticky === 'right' ? 0 : undefined,
                                    width,
                                    zIndex: sticky ? 3 : undefined,
                                  },
                                  onClickRow ? { cursor: 'pointer' } : null,
                                ]}
                              >
                                {cellData}
                              </TableCell>
                            );
                          },
                        )}
                      </TableRow>
                    );
                  })
                )}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: MIN_ROW_HEIGHT * emptyRows,
                    }}
                  >
                    <TableCell colSpan={columns.length} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </MuiTable>
        {infiniteScrollListener && !isLoading && (
          <Box display='flex' justifyContent='center' padding='32px'>
            {infiniteScrollListener}
          </Box>
        )}
      </TableContainer>
      {hasMultiplePages && (
        <PaginationContainer>
          <Pagination
            count={Math.ceil(rows.length / rowsPerPage)}
            page={pageValue}
            onChange={(_, newPage) => {
              setPage(newPage);
              if (onSetPageCallback) {
                onSetPageCallback(newPage);
              }
            }}
          />
        </PaginationContainer>
      )}
    </>
  );
}

const PaginationContainer = styled('div')`
  display: flex;
  justify-content: center;
  padding-top: 16px;
`;
